import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Alert,
  Box,
  Typography,
  Stack,
  LinearProgress,
  Divider,
  CircularProgress,
  Paper,
  AlertTitle,
} from '@mui/material';
import {
  Close,
  AccountBalance,
  CreditCard,
  AttachMoney,
  Warning,
  InfoOutlined,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import client from '../../../../../API';
import { replaceConsoleError } from '../../../../../Utils/toastNotifications';

const BudgetDialog = ({ 
  open, 
  onClose, 
  castingCall,
  onSuccess,
}) => {
  const queryClient = useQueryClient();
  
  // Local state
  const [newBudget, setNewBudget] = useState('');
  const [error, setError] = useState(null);
  const [budgetValidation, setBudgetValidation] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // Get current budget info
  const currentBudget = castingCall?.budget || 0;

  // Load budget status
  const { data: budgetStatus, isLoading: loadingBudget } = useQuery(
    ['castingCallBudget', castingCall?.id],
    () => client.castingCalls.getBudgetStatus(castingCall.id),
    {
      enabled: open && !!castingCall?.id,
      refetchOnWindowFocus: false,
      select: (data) => data.budget_status,
    }
  );

  // Initialize new budget to current budget
  useEffect(() => {
    if (open && currentBudget) {
      setNewBudget(currentBudget.toString());
    }
  }, [open, currentBudget]);

  // Validate budget when amount changes
  useEffect(() => {
    const amount = parseFloat(newBudget);
    if (amount > 0 && amount !== currentBudget && open) {
      validateBudget(amount);
    } else {
      setBudgetValidation(null);
    }
  }, [newBudget, currentBudget, open]);

  // Validate budget API call
  const validateBudget = async (amount) => {
    if (!amount || amount <= 0) return;
    
    setIsValidating(true);
    setError(null);
    
    try {
      const response = await client.castingCalls.validateBudget(castingCall.id, amount);
      setBudgetValidation(response.validation);
    } catch (err) {
      replaceConsoleError('Budget validation failed', err);
      setBudgetValidation(null);
      setError(err.response?.data?.error || 'Failed to validate budget');
    } finally {
      setIsValidating(false);
    }
  };

  // Update budget mutation
  const { mutate: updateBudget, isLoading: isUpdating } = useMutation(
    (amount) => client.castingCalls.updateBudget(castingCall.id, { budget: amount }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['castingCall', castingCall.id]);
        queryClient.invalidateQueries(['castingCallBudget', castingCall.id]);
        
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      },
      onError: (err) => {
        replaceConsoleError('Failed to update budget', err);
        setError(err.response?.data?.error || 'Failed to update budget');
      },
    }
  );

  // Calculate differences
  const budgetDifference = useMemo(() => {
    const newAmt = parseFloat(newBudget) || 0;
    return newAmt - currentBudget;
  }, [newBudget, currentBudget]);

  const isIncrease = budgetDifference > 0;
  const isDecrease = budgetDifference < 0;

  // Validation checks
  const validationError = useMemo(() => {
    const newAmt = parseFloat(newBudget);

    if (!newAmt || newAmt <= 0) {
      return { message: 'Please enter a valid budget amount', severity: 'error' };
    }

    if (newAmt === currentBudget) {
      return { message: 'New budget must be different from current budget', severity: 'info' };
    }

    if (budgetStatus && newAmt < parseFloat(budgetStatus.allocated)) {
      return { 
        message: `Cannot reduce budget below allocated amount ($${budgetStatus.allocated})`, 
        severity: 'error' 
      };
    }

    if (isIncrease && budgetValidation && !budgetValidation.can_fund) {
      return {
        message: 'Insufficient funds to increase budget by this amount',
        severity: 'error'
      };
    }

    return null;
  }, [newBudget, currentBudget, budgetStatus, budgetValidation, isIncrease]);

  const handleSubmit = () => {
    setError(null);
    
    if (validationError) {
      setError(validationError.message);
      return;
    }

    const newAmt = parseFloat(newBudget);
    updateBudget(newAmt);
  };

  const handleClose = () => {
    setNewBudget('');
    setError(null);
    setBudgetValidation(null);
    onClose();
  };

  const canSubmit = !validationError && !isUpdating && !isValidating && newBudget;

  if (!castingCall) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AttachMoney color="primary" />
            <Typography variant="h6">Update Casting Call Budget</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Current Budget Overview */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Current Budget Status
            </Typography>
            
            {loadingBudget ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : budgetStatus ? (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Stack spacing={1.5}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Total Budget</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      ${parseFloat(budgetStatus.budget).toFixed(2)}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      Allocated (Payouts)
                    </Typography>
                    <Typography variant="caption" color="warning.main" fontWeight={600}>
                      ${parseFloat(budgetStatus.allocated).toFixed(2)}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                      Remaining
                    </Typography>
                    <Typography variant="caption" color="success.main" fontWeight={600}>
                      ${parseFloat(budgetStatus.remaining).toFixed(2)}
                    </Typography>
                  </Box>
                  
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(
                      (parseFloat(budgetStatus.allocated) / parseFloat(budgetStatus.budget)) * 100,
                      100
                    )}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      mt: 1,
                    }}
                  />
                </Stack>
              </Paper>
            ) : null}
          </Box>

          <Divider />

          {/* New Budget Input */}
          <TextField
            fullWidth
            label="New Budget Amount"
            type="number"
            value={newBudget}
            onChange={(e) => setNewBudget(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            disabled={isUpdating}
            autoFocus
            error={!!validationError}
            helperText={
              budgetDifference !== 0 && !validationError
                ? `${isIncrease ? 'Increase' : 'Decrease'} of $${Math.abs(budgetDifference).toFixed(2)}`
                : null
            }
          />

          {/* Budget Change Info */}
          {budgetDifference !== 0 && (
            <Alert 
              severity={isIncrease ? 'info' : 'warning'}
              icon={isIncrease ? <InfoOutlined /> : <Warning />}
            >
              <AlertTitle>
                {isIncrease ? 'Budget Increase' : 'Budget Decrease'}
              </AlertTitle>
              {isIncrease ? (
                <>
                  Increasing budget by ${Math.abs(budgetDifference).toFixed(2)} 
                  {budgetValidation && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      This will be funded from your available balance/credit
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  Decreasing budget by ${Math.abs(budgetDifference).toFixed(2)}
                  {budgetStatus && parseFloat(budgetStatus.allocated) > 0 && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      ⚠️ Make sure this covers all allocated payouts (${budgetStatus.allocated})
                    </Typography>
                  )}
                </>
              )}
            </Alert>
          )}

          {/* Available Funds (for increases) */}
          {isIncrease && (
            <>
              {isValidating ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : budgetValidation ? (
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InfoOutlined fontSize="small" color="primary" />
                    Available Funding Sources
                  </Typography>
                  
                  <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={1}>
                        <AccountBalance fontSize="small" color="primary" />
                        <Typography variant="body2">Personal Balance</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        ${parseFloat(budgetValidation.available_balance).toFixed(2)}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={1}>
                        <CreditCard fontSize="small" color="info" />
                        <Typography variant="body2">Spending Power</Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={600}>
                        ${parseFloat(budgetValidation.available_credit).toFixed(2)}
                      </Typography>
                    </Box>
                    
                    <Divider />
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={600}>
                        Total Available
                      </Typography>
                      <Typography 
                        variant="body2" 
                        fontWeight={600}
                        color={budgetValidation.can_fund ? 'success.main' : 'error.main'}
                      >
                        ${parseFloat(budgetValidation.total_available).toFixed(2)}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Funding Source Recommendation */}
                  {budgetValidation.can_fund && budgetValidation.recommended_source && (
                    <Alert severity="success" sx={{ mt: 2 }} icon={false}>
                      <Typography variant="caption">
                        Recommended: Fund from {
                          budgetValidation.recommended_source === 'balance' ? 'Personal Balance' :
                          budgetValidation.recommended_source === 'credit_line' ? 'Spending Power' :
                          'Both Sources'
                        }
                      </Typography>
                    </Alert>
                  )}

                  {/* Warnings */}
                  {budgetValidation.warnings && budgetValidation.warnings.length > 0 && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {budgetValidation.warnings.map((warning, idx) => (
                        <Typography key={idx} variant="caption" display="block">
                          • {warning}
                        </Typography>
                      ))}
                    </Alert>
                  )}
                </Paper>
              ) : null}
            </>
          )}

          {/* Validation Error */}
          {validationError && (
            <Alert severity={validationError.severity} icon={<ErrorIcon />}>
              {validationError.message}
            </Alert>
          )}

          {/* Error Display */}
          {error && (
            <Alert 
              severity="error"
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isUpdating && <CircularProgress size={16} />}
        >
          {isUpdating 
            ? 'Updating...' 
            : isIncrease 
            ? 'Increase Budget' 
            : isDecrease 
            ? 'Decrease Budget'
            : 'Update Budget'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BudgetDialog;